import { format } from 'date-fns';

import { DEFAULT_DATETIME_FORMAT } from '../constants';

/**
 * Parse a timestamp as this API renders them.
 *
 * DRF is configured with a global non-ISO output format
 * (dashboard_project/settings.py):
 *
 *   'DATETIME_FORMAT': '%m/%d/%Y, %H:%M:%S %z'   ->  "08/24/2026, 11:59:00 -0400"
 *
 * That breaks the obvious approaches:
 *  - date-fns' `parseJSON` (what the shared `formatDate` util uses internally)
 *    returns Invalid Date for it.
 *  - Bare `new Date(text)` happens to work, but only because V8 has a lenient
 *    fallback parser for non-standard strings. Engines are free to return NaN,
 *    so it is not something to rely on.
 *
 * So parse the parts explicitly. The trailing offset is applied, which puts the
 * result on the correct instant regardless of where the reader is.
 */
export const parseApiDateTime = (
  value?: string | null,
): Date | null => {
  if (!value) return null;

  const text = String(value).trim();
  const parts = text.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([+-])(\d{2}):?(\d{2}))?$/,
  );

  if (parts) {
    const year = +parts[3];
    const month = +parts[1] - 1;
    const day = +parts[2];
    const hours = +parts[4];
    const minutes = +parts[5];
    const seconds = +(parts[6] || 0);
    const sign = parts[7];

    if (!sign) {
      // No offset in the string: read it as the reader's own clock, which is
      // what a naive server datetime means.
      return new Date(
        year,
        month,
        day,
        hours,
        minutes,
        seconds,
      );
    }

    const offsetMinutes =
      (sign === '-' ? -1 : 1) *
      (+parts[8] * 60 + +parts[9]);

    return new Date(
      Date.UTC(year, month, day, hours, minutes, seconds) -
        offsetMinutes * 60000,
    );
  }

  // ISO-8601 and anything else the platform handles properly.
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * "11:59 AM 08/24/2026" - the app-wide DEFAULT_DATETIME_FORMAT.
 * Returns '' rather than "Invalid Date" when the value can't be read.
 */
export const formatApiDateTime = (
  value?: string | null,
): string => {
  const parsed = parseApiDateTime(value);
  return parsed ? format(parsed, DEFAULT_DATETIME_FORMAT) : '';
};

export default formatApiDateTime;
